import { Component, HostListener, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Socket } from 'socket.io-client';
import { AnimationOptions } from 'ngx-lottie';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import * as typingAnimationData from '../../assets/animation/typing_effect.json';
import { ChatMessageItem, ChatStaffListItem, ParentChatSocketService } from './parent-chat-socket.service';

declare var customToastr: any;
function safeToast(message: string, type: 'success' | 'error' | 'info' = 'info') {
  if (typeof customToastr === 'function') { customToastr(message, type); }
}

interface ChatDateBucket {
  date: string; // YYYY-MM-DD
  messages: ChatMessageItem[];
}

function todayDateOnly(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function dateOnlyFromTimestamp(ts: string): string {
  const d = new Date(ts);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function dayLabel(dateStr: string, todayLabel: string, yesterdayLabel: string): string {
  const today = todayDateOnly();
  const yesterday = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();
  if (dateStr === today) { return todayLabel; }
  if (dateStr === yesterday) { return yesterdayLabel; }
  return new Date(dateStr).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Chat — parent<->staff messaging, scoped per student (ChatServices.js +
// ChatScreen.js). Business logic/UI ported 1:1 from StaffClassChatComponent
// (staff<->student), the confirmed-working reference implementation for
// this same Socket.IO chat layer — only the list's field names/event names
// differ (get_staff_list/staff_list_response, staffId/staffName/
// designation/staffImage instead of student-side names), per the API
// reference this was built from.
@Component({
  selector: 'app-parent-chat',
  templateUrl: './parent-chat.component.html',
  styleUrls: ['./parent-chat.component.css'],
})
export class ParentChatComponent implements OnInit, OnChanges, OnDestroy {

  @Input() studentId = '';
  @Input() classId = '';
  @Input() studentSwitchTick = 0;
  // When set (e.g. from My Teacher's Message button) that staff member's conversation opens once the list loads.
  @Input() openStaffId: string | null = null;
  private openStaffHandled = false;

  // Set only while switching from one open conversation straight to another
  // (reconnectAndOpen) — the fresh socket's staff_list_response handler
  // opens this staff member's conversation once the new connection is
  // authorized, the same way openStaffId does for an external deep link,
  // but scoped to this one-time post-reconnect hop instead of the
  // component's whole lifetime.
  private pendingStaffIdAfterReconnect: string | null = null;

  // True only for the brief reconnect gap between leaving the old
  // conversation and the new one's openConversation() re-running —
  // selectedStaff is null during that window, which would otherwise fall
  // through to the "select a conversation" empty state in the right pane
  // and look like the switch silently failed. Drives a loader there
  // instead, scoped to the chat window only (the list pane is untouched).
  switchingConversation = false;

  isConnected = false;
  loading = true;
  staffList: ChatStaffListItem[] = [];

  searchQuery = '';
  activeFilter: 'ALL' | 'UNREAD' | 'TEACHERS' | 'SCHOOL_OFFICE' = 'ALL';

  selectedStaff: ChatStaffListItem | null = null;
  userOnline = false;
  isTyping = false; // the other party's typing state
  chatExceeded = false;
  chatExceededMessage = '';

  typingAnimationOptions: AnimationOptions = {
    animationData: typingAnimationData,
    loop: true,
    autoplay: true,
  };

  chatBuckets: ChatDateBucket[] = [];
  chatMessage = '';
  page = 1;
  paginationLoading = false;
  conversationLoading = true;

  private socket: Socket | null = null;
  private typingStopTimer: any;
  private typingHideTimer: any;
  private newGroupId: string | null = null;

  private todayLabel = 'Today';
  private yesterdayLabel = 'Yesterday';

  constructor(private chatSocketService: ParentChatSocketService, private translate: TranslateService) { }

  ngOnInit(): void {
    this.translate.get(['today', 'yesterday']).subscribe((translations: { [key: string]: string }) => {
      this.todayLabel = translations['today'];
      this.yesterdayLabel = translations['yesterday'];
    });

    this.socket = this.chatSocketService.connect();
    this.wireServiceEvents();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['studentSwitchTick'] && !changes['studentSwitchTick'].firstChange) {
      if (this.selectedStaff) {
        this.closeConversation();
      } else {
        this.getStaffList();
      }
    }
  }

  ngOnDestroy(): void {
    if (this.selectedStaff) {
      this.leaveConversationRoom();
    }
    clearTimeout(this.typingStopTimer);
    clearTimeout(this.typingHideTimer);
    this.chatSocketService.disconnect();
  }

  private wireServiceEvents() {
    if (!this.socket) { return; }

    this.socket.on('connect', () => { this.isConnected = true; });
    this.socket.on('disconnect', () => { this.isConnected = false; });

    this.socket.on('authorized', (data: { status: boolean }) => {
      if (data?.status) { this.getStaffList(); }
    });

    this.socket.on('unauthorized', () => {
      // No "back" destination on this side (unlike staff's goBack() to the
      // class screen) — the parent stays on the chat screen with an empty
      // list; the socket service itself will have already been told to
      // disconnect by the server closing the connection.
      this.loading = false;
    });

    this.socket.on('staff_list_response', (data: { staffData: ChatStaffListItem[] }) => {
      this.staffList = data?.staffData || [];
      this.loading = false;

      if (this.pendingStaffIdAfterReconnect) {
        const pendingId = this.pendingStaffIdAfterReconnect;
        this.pendingStaffIdAfterReconnect = null;
        const target = this.staffList.find(st => String(st.staffId) === String(pendingId));
        if (target) {
          this.openConversation(target);
        } else {
          // Target vanished from the list across the reconnect (e.g. no
          // longer assigned to this class) — stop showing the switch
          // loader rather than leaving it spinning forever.
          this.switchingConversation = false;
        }
        return;
      }

      if (this.openStaffId && !this.openStaffHandled && !this.selectedStaff) {
        const target = this.staffList.find(st => String(st.staffId) === String(this.openStaffId));
        if (target) {
          this.openStaffHandled = true;
          this.openConversation(target);
        }
      }
    });

    this.socket.on('chat_feature_disabled', async (data: { message: string }) => {
      const chatIsCurrentlyDisabledTranslation = await firstValueFrom(this.translate.get('chatIsCurrentlyDisabled'));
      safeToast(data?.message || chatIsCurrentlyDisabledTranslation, 'error');
    });

    this.socket.on('update_list', (data: { updateList: boolean }) => {
      if (data?.updateList) { this.getStaffList(); }
    });

    this.socket.on('update_typing_response', (data: { userId: string; typing: boolean }) => {
      const staff = this.staffList.find(s => s.staffId === data.userId);
      if (staff) { staff.isTyping = data.typing; }
    });

    // ---- Conversation-screen events (only relevant once a chat is open) ----
    this.socket.on('initiate_chat_success', (data: { groupId: string }) => {
      console.log(`[chat] [t=${Date.now()}] received initiate_chat_success`, data);
      this.newGroupId = data.groupId;
      this.page = 1;
      this.chatBuckets = [];
      this.getMessageData(1);
      this.getUserStatus();
      this.chatRead();
    });

    this.socket.on('user_online_response', () => { this.userOnline = true; });
    this.socket.on('user_offline_response', () => { this.userOnline = false; });

    this.socket.on('chat_message_response', (data: { messageHistoryData: ChatMessageItem[] }) => {
      const isFirstPage = this.page === 1;
      this.mergeMessagePage(data?.messageHistoryData || []);
      this.conversationLoading = false;
      this.paginationLoading = false;
      if (isFirstPage) { this.scrollToBottom(); }
    });

    this.socket.on('chat_limit_exceeded', async (data: { chatLimitExceededMessage: string }) => {
      this.chatExceeded = true;
      const messageLimitReachedForThisConversationTranslation = await firstValueFrom(this.translate.get('messageLimitReachedForThisConversation'));
      this.chatExceededMessage = data?.chatLimitExceededMessage || messageLimitReachedForThisConversationTranslation;
    });

    this.socket.on('message_sent_success', (message: ChatMessageItem) => {
      this.reconcileMessage(message);
      this.scrollToBottom();
      if (!this.isOwnMessage(message.fromUserId)) {
        this.chatRead();
      }
    });

    this.socket.on('message_read_success', () => {
      this.chatBuckets.forEach(bucket => bucket.messages.forEach(m => { m.IsRead = 1; }));
      if (this.selectedStaff) {
        const row = this.staffList.find(s => s.staffId === this.selectedStaff!.staffId);
        if (row) {
          row.IsRead = 1;
          row.unreadCount = 0;
        }
      }
    });

    this.socket.on('typing_response', () => {
      clearTimeout(this.typingHideTimer);
      const wasTyping = this.isTyping;
      this.isTyping = true;
      if (!wasTyping) { this.scrollToBottom(); }
    });
    this.socket.on('typing_stopped_response', () => {
      // Hold the indicator briefly instead of hiding it instantly — avoids
      // flicker if the other party resumes typing right away.
      clearTimeout(this.typingHideTimer);
      this.typingHideTimer = setTimeout(() => { this.isTyping = false; }, 1000);
    });
  }

  getStaffList() {
    if (!this.socket || !this.classId) { return; }
    // Only show the skeleton on the very first load — a background refresh
    // (e.g. update_list after a new message) should update the list in
    // place without flashing the loader over already-visible rows.
    if (!this.staffList.length) { this.loading = true; }
    this.socket.emit('get_staff_list', { classid: this.classId });
  }

  // ---------------- List item display helpers ----------------

  // Socket payloads aren't type-checked at runtime, so fromUserId can arrive
  // as a number even though the TS interface declares it a string — compare
  // as strings everywhere to avoid a silent === mismatch.
  isOwnMessage(fromUserId: string | number): boolean {
    return !!this.studentId && String(fromUserId) === String(this.studentId);
  }

  showsStatusIcon(item: ChatStaffListItem): boolean {
    return this.isOwnMessage(item.fromUserId);
  }

  unreadDisplay(count: number): string {
    return count > 99 ? '99+' : String(count);
  }

  // No discriminator field exists on the server for "teacher" vs. "school
  // office" staff — only a free-text `designation` string. Heuristic: a
  // handful of non-teaching keywords mark a row as "School office"; every
  // other designation falls under "Teachers". Good enough for client-side
  // filtering without a backend change.
  private isSchoolOfficeDesignation(designation: string | null | undefined): boolean {
    const d = (designation || '').toLowerCase();
    return /principal|admin|office|coordinator|accountant|transport|reception|management|director/.test(d);
  }

  isTeacherStaff(item: ChatStaffListItem): boolean {
    return !this.isSchoolOfficeDesignation(item.designation);
  }

  isSchoolOfficeStaff(item: ChatStaffListItem): boolean {
    return this.isSchoolOfficeDesignation(item.designation);
  }

  setFilter(filter: 'ALL' | 'UNREAD' | 'TEACHERS' | 'SCHOOL_OFFICE'): void {
    this.activeFilter = filter;
  }

  get unreadConversationCount(): number {
    return this.staffList.filter(s => s.unreadCount > 0).length;
  }

  get filteredStaffList(): ChatStaffListItem[] {
    let list = this.staffList;
    if (this.activeFilter === 'UNREAD') {
      list = list.filter(s => s.unreadCount > 0);
    } else if (this.activeFilter === 'TEACHERS') {
      list = list.filter(s => this.isTeacherStaff(s));
    } else if (this.activeFilter === 'SCHOOL_OFFICE') {
      list = list.filter(s => this.isSchoolOfficeStaff(s));
    }
    const query = this.searchQuery.trim().toLowerCase();
    if (!query) { return list; }
    return list.filter(s =>
      (s.staffName || '').toLowerCase().includes(query) ||
      (s.designation || '').toLowerCase().includes(query) ||
      (s.message || '').toLowerCase().includes(query)
    );
  }

  // ---------------- Conversation ----------------

  openConversation(staff: ChatStaffListItem) {
    if (!this.socket || !this.classId || !this.studentId) { return; }

    // Switching directly from one open conversation to another (the
    // two-pane layout makes this reachable without going through
    // closeConversation() first, unlike the old single-column view-swap,
    // which only ever re-mounted a fresh ChatScreen after the user had
    // already navigated back to the list — see ChatScreen.js/
    // ChatServices.js). A same-socket leave_room followed shortly by
    // initiate_chat for a different receiver was found to be unreliable:
    // initiate_chat_success for the new conversation sometimes never
    // arrives even with a delay, since there's no server ack for leave_room
    // to actually wait on, only a guess at how long its cleanup takes.
    // Reconnecting the socket on switch sidesteps that entirely — the new
    // conversation starts on a fresh connection with guaranteed clean
    // server-side state, the same guarantee a fresh ChatScreen mount gave
    // on mobile, instead of reusing one socket's leftover room state.
    if (this.selectedStaff && this.selectedStaff.staffId !== staff.staffId) {
      this.reconnectAndOpen(staff);
      return;
    }

    this.switchingConversation = false;
    this.selectedStaff = staff;
    this.newGroupId = null;
    this.userOnline = false;
    clearTimeout(this.typingHideTimer);
    this.isTyping = false;
    this.chatExceeded = false;
    this.chatExceededMessage = '';
    this.chatBuckets = [];
    this.page = 1;
    this.conversationLoading = true;

    this.socket.emit('initiate_chat', {
      receiverid: staff.staffId,
      groupid: staff.fkChatGroupId || 'NA',
      senderid: this.studentId,
      classid: this.classId,
    });
  }

  // Leaves the current conversation's room, tears down the socket entirely,
  // then opens a brand-new connection and re-runs the same
  // authorized -> get_staff_list -> staff_list_response handshake ngOnInit
  // does — once that response lands, pendingStaffIdAfterReconnect tells it
  // to open `staff`'s conversation immediately, same as the openStaffId
  // deep-link path.
  private reconnectAndOpen(staff: ChatStaffListItem): void {
    this.leaveConversationRoom();

    this.switchingConversation = true;
    this.selectedStaff = null;
    this.newGroupId = null;
    this.userOnline = false;
    clearTimeout(this.typingHideTimer);
    this.isTyping = false;
    this.chatExceeded = false;
    this.chatExceededMessage = '';
    this.chatBuckets = [];
    this.page = 1;
    this.conversationLoading = true;
    // Deliberately NOT forcing `loading = true` here — the reconnect is an
    // implementation detail of the socket/room-state cleanup, not a real
    // list refresh from the user's perspective. staffList already has data
    // from the previous connection, so keep showing it as-is; getStaffList()
    // only shows the skeleton when staffList is empty, so the fresh
    // staff_list_response below will just replace the rows in place.
    this.pendingStaffIdAfterReconnect = String(staff.staffId);

    this.chatSocketService.disconnect();
    this.socket = this.chatSocketService.connect();
    this.wireServiceEvents();
  }

  private getMessageData(page: number) {
    if (!this.socket || !this.selectedStaff || !this.studentId) { return; }
    this.socket.emit('get_chat_message', {
      senderid: this.studentId,
      receiverid: this.selectedStaff.staffId,
      groupid: this.newGroupId,
      page,
    });
  }

  private getUserStatus() {
    if (!this.socket || !this.selectedStaff || !this.studentId) { return; }
    this.socket.emit('receiver_status', {
      groupid: this.newGroupId,
      senderid: this.studentId,
      receiverid: this.selectedStaff.staffId,
    });
  }

  private chatRead() {
    if (!this.socket || !this.selectedStaff || !this.studentId) { return; }
    this.socket.emit('message_read', {
      senderid: this.studentId,
      receiverid: this.selectedStaff.staffId,
      groupid: this.newGroupId,
    });
  }

  // Groups incoming messages by date, dedupes by messageId, and prepends
  // each date's new (older) messages before that bucket's existing ones —
  // matches the mobile app's pagination-merge semantics.
  private mergeMessagePage(incoming: ChatMessageItem[]) {
    const byDate = new Map<string, ChatMessageItem[]>();
    incoming.forEach(msg => {
      const dateKey = dateOnlyFromTimestamp(msg.timeStamp);
      if (!byDate.has(dateKey)) { byDate.set(dateKey, []); }
      byDate.get(dateKey)!.push(msg);
    });

    byDate.forEach((newMessages, dateKey) => {
      let bucket = this.chatBuckets.find(b => b.date === dateKey);
      if (!bucket) {
        bucket = { date: dateKey, messages: [] };
        this.chatBuckets.push(bucket);
      }
      const existingIds = new Set(bucket.messages.filter(m => m.messageId).map(m => m.messageId));
      const newUniqueMessages = newMessages.filter(m => !m.messageId || !existingIds.has(m.messageId));
      bucket.messages = [...newUniqueMessages, ...bucket.messages];
    });

    this.chatBuckets.sort((a, b) => (a.date < b.date ? -1 : 1));
  }

  private addOptimisticMessage(uid: string, text: string) {
    const dateKey = todayDateOnly();
    let bucket = this.chatBuckets.find(b => b.date === dateKey);
    if (!bucket) {
      bucket = { date: dateKey, messages: [] };
      this.chatBuckets.unshift(bucket);
      this.chatBuckets.sort((a, b) => (a.date < b.date ? -1 : 1));
    }
    const timeStamp = new Date().toISOString();
    bucket.messages.push({
      messageId: null,
      message: text,
      fromUserId: String(this.studentId || ''),
      timeStamp,
      uid,
      IsRead: 0,
    });
    this.syncStaffListPreview(this.selectedStaff, text, timeStamp, String(this.studentId || ''), 0);
  }

  // The left-pane list only otherwise refreshes on the server's `update_list`
  // push — without this, a just-sent or just-received message updates the
  // open thread but leaves that staff's list row (preview text, timestamp,
  // ordering) stale until that push arrives, which is now visible since
  // both panes render at once. Keeps the matching row's preview in sync
  // immediately, same data reconcileMessage/addOptimisticMessage already
  // has in hand.
  private syncStaffListPreview(staff: ChatStaffListItem | null, message: string, timeStamp: string, fromUserId: string, isRead: 0 | 1): void {
    if (!staff) { return; }
    const index = this.staffList.findIndex(s => s.staffId === staff.staffId);
    if (index === -1) { return; }
    const row = this.staffList[index];
    row.message = message;
    row.timeStamp = timeStamp;
    row.fromUserId = fromUserId;
    row.IsRead = isRead;
    if (!this.isOwnMessage(fromUserId)) {
      row.unreadCount = (row.unreadCount || 0) + 1;
    }
    // Bring the just-active conversation to the top, matching the server's
    // own most-recent-first ordering convention for staff_list_response.
    if (index > 0) {
      this.staffList.splice(index, 1);
      this.staffList.unshift(row);
    }
  }

  private reconcileMessage(message: ChatMessageItem) {
    if (!message || !message.timeStamp) { return; }

    const dateKey = dateOnlyFromTimestamp(message.timeStamp);
    let bucket = this.chatBuckets.find(b => b.date === dateKey);

    if (bucket) {
      const idx = bucket.messages.findIndex(m => m.uid === message.uid);
      if (idx !== -1) {
        // Own message being confirmed: overwrite the placeholder in place.
        bucket.messages[idx] = { ...bucket.messages[idx], ...message };
        this.syncStaffListPreview(this.selectedStaff, message.message, message.timeStamp, String(message.fromUserId), message.IsRead);
        return;
      }
      // Incoming message from the other party — no local echo to reconcile,
      // append fresh at the end (newest).
      bucket.messages = [...bucket.messages, message];
      this.syncStaffListPreview(this.selectedStaff, message.message, message.timeStamp, String(message.fromUserId), message.IsRead);
      return;
    }

    bucket = { date: dateKey, messages: [message] };
    this.chatBuckets.push(bucket);
    this.chatBuckets.sort((a, b) => (a.date < b.date ? -1 : 1));
    this.syncStaffListPreview(this.selectedStaff, message.message, message.timeStamp, String(message.fromUserId), message.IsRead);
  }

  sendMessage() {
    const text = this.chatMessage.trim();
    if (!text || !this.socket || !this.selectedStaff || !this.studentId || !this.classId) { return; }
    const uid = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const timestamp = new Date().toISOString();

    this.socket.emit('send_message', {
      senderid: this.studentId,
      receiverid: this.selectedStaff.staffId,
      classid: this.classId,
      groupid: this.newGroupId,
      message: text,
      timestamp,
      uid,
    });

    this.addOptimisticMessage(uid, text);
    this.chatMessage = '';
    this.stopTyping();
    this.scrollToBottom();
  }

  onTyping() {
    if (!this.socket || !this.selectedStaff || !this.studentId) { return; }
    this.socket.emit('typing', {
      groupid: this.newGroupId,
      senderid: this.studentId,
      receiverid: this.selectedStaff.staffId,
    });
    clearTimeout(this.typingStopTimer);
    this.typingStopTimer = setTimeout(() => this.stopTyping(), 2000);
  }

  private stopTyping() {
    if (!this.socket || !this.selectedStaff || !this.studentId) { return; }
    this.socket.emit('typing_stopped', {
      groupid: this.newGroupId,
      senderid: this.studentId,
      receiverid: this.selectedStaff.staffId,
    });
  }

  private leaveConversationRoom() {
    if (!this.socket || !this.selectedStaff || !this.studentId) { return; }
    const payload = {
      senderid: this.studentId,
      receiverid: this.selectedStaff.staffId,
      groupid: this.newGroupId,
    };
    console.log(`[chat] [t=${Date.now()}] emit leave_room`, payload);
    this.socket.emit('leave_room', payload);
  }

  closeConversation() {
    this.leaveConversationRoom();
    this.selectedStaff = null;
    this.switchingConversation = false;
    this.pendingStaffIdAfterReconnect = null;
    this.getStaffList();
  }

  // Esc closes just the open conversation (leaves the room, same as the
  // explicit close action) — the two-pane layout has no visible "close"
  // button of its own, so this is the only non-click way out of a thread.
  // Scoped to only fire while a conversation is actually open, so Esc does
  // nothing when the list is the only thing showing.
  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.selectedStaff) {
      this.closeConversation();
    }
  }

  loadOlderMessages() {
    if (this.paginationLoading || !this.selectedStaff) { return; }
    this.paginationLoading = true;
    this.page += 1;
    this.getMessageData(this.page);
  }

  onMessagesScroll(event: Event) {
    const el = event.target as HTMLElement;
    if (el.scrollTop < 80) { this.loadOlderMessages(); }
  }

  private scrollToBottom() {
    setTimeout(() => {
      const el = document.getElementById('pch-chat-scroll');
      if (el) { el.scrollTop = el.scrollHeight; }
    });
  }

  dayLabelFor(dateStr: string): string {
    return dayLabel(dateStr, this.todayLabel, this.yesterdayLabel);
  }

  // Stable identity across the pending -> confirmed reconciliation, so
  // Angular patches the existing DOM node instead of destroying/recreating
  // it (which would replay the slide-in entrance animation as a flicker).
  trackByMessageUid(_index: number, m: ChatMessageItem): string {
    return m.uid;
  }

  trackByBucketDate(_index: number, bucket: ChatDateBucket): string {
    return bucket.date;
  }

  messageStatus(m: ChatMessageItem): 'pending' | 'sent' | 'read' | null {
    if (!this.isOwnMessage(m.fromUserId)) { return null; }
    if (m.messageId === null) { return 'pending'; }
    return m.IsRead === 1 ? 'read' : 'sent';
  }

  formatTime(ts: string): string {
    return new Date(ts).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }
}
